import { useCallback, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { invalidateCampaignQueries } from '#/entities/campaign'
import type { CampaignRow } from '#/entities/campaign'
import {
  useDeleteCampaignApiV1CampaignsCampaignIdDelete,
  useDuplicateCampaignApiV1CampaignsCampaignIdDuplicatePost,
  usePauseCampaignApiV1CampaignsCampaignIdPausePost,
  useStartCampaignApiV1CampaignsCampaignIdStartPost,
  useStopCampaignApiV1CampaignsCampaignIdStopPost,
} from '#/shared/api/generated/communication/theCargoCommunication'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { CAMPAIGN_DETAIL_DEFAULT_TAB } from './detail-tabs'
import { CAMPAIGN_LIFECYCLE_ACTIONS } from './use-campaign-actions'
import type { CampaignLifecycleAction } from './use-campaign-actions'

export interface CampaignRowActions {
  onEdit: (row: CampaignRow) => void
  onDuplicate: (row: CampaignRow) => void
  onLifecycle: (row: CampaignRow, action: CampaignLifecycleAction) => void
  onStop: (row: CampaignRow) => void
  /** Opens the confirm dialog; nothing is sent until it is answered. */
  onDeleteRequest: (row: CampaignRow) => void
  isBusy: boolean
}

export interface UseCampaignRowActionsResult {
  actions: CampaignRowActions
  /** The campaign the confirm dialog is asking about, or `null` when closed. */
  deleteTarget: CampaignRow | null
  onDeleteCancel: () => void
  onDeleteConfirm: () => void
  isDeleting: boolean
}

/**
 * Every campaign write the row menu can make, held once for the whole table.
 *
 * The mutations take their id at `mutate` time, so one set serves every row —
 * a hook per row would mount five mutations per visible campaign for menus
 * that are almost never opened.
 */
function useCampaignWrites(onDeleted: () => void) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const fail = useCallback(
    (error: unknown) =>
      toast.error(
        getApiErrorDetail(error) ?? t('campaigns.detail.actionError'),
      ),
    [t],
  )
  const settle = useCallback(
    () => invalidateCampaignQueries(queryClient),
    [queryClient],
  )
  const announce = useCallback(
    (message: string) => () => {
      toast.success(message)
      settle()
    },
    [settle],
  )

  return {
    fail,
    settle,
    pause: usePauseCampaignApiV1CampaignsCampaignIdPausePost({
      mutation: {
        onSuccess: announce(t('campaigns.detail.toasts.paused')),
        onError: fail,
      },
    }),
    start: useStartCampaignApiV1CampaignsCampaignIdStartPost({
      mutation: {
        onSuccess: announce(t('campaigns.detail.toasts.started')),
        onError: fail,
      },
    }),
    stop: useStopCampaignApiV1CampaignsCampaignIdStopPost({
      mutation: {
        onSuccess: announce(t('campaigns.detail.toasts.stopped')),
        onError: fail,
      },
    }),
    duplicate: useDuplicateCampaignApiV1CampaignsCampaignIdDuplicatePost({
      mutation: { onError: fail },
    }),
    remove: useDeleteCampaignApiV1CampaignsCampaignIdDelete({
      mutation: {
        onSuccess: () => {
          toast.success(t('campaigns.detail.toasts.deleted'))
          onDeleted()
          settle()
        },
        onError: fail,
      },
    }),
  }
}

/**
 * The campaign list's per-row menu.
 *
 * Delete is the one action that does not fire on click: it parks the row in
 * `deleteTarget` and waits for the dialog, because a campaign mid-sequence
 * takes its enrollments and its send history with it.
 */
export function useCampaignRowActions(): UseCampaignRowActionsResult {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [deleteTarget, setDeleteTarget] = useState<CampaignRow | null>(null)
  const clearTarget = useCallback(() => setDeleteTarget(null), [])
  const writes = useCampaignWrites(clearTarget)

  const openDetail = useCallback(
    (campaignId: string) =>
      void navigate({
        to: '/campaign/$campaignId',
        params: { campaignId },
        search: { tab: CAMPAIGN_DETAIL_DEFAULT_TAB },
      }),
    [navigate],
  )

  const { pause, start, stop, duplicate, remove, settle } = writes

  const onDuplicate = useCallback(
    (row: CampaignRow) =>
      duplicate.mutate(
        { campaignId: row.id },
        {
          /** A copy is only useful open — the agent duplicated it to edit it. */
          onSuccess: (response) => {
            toast.success(t('campaigns.detail.toasts.duplicated'))
            settle()
            openDetail(response.data.id)
          },
        },
      ),
    [duplicate, t, settle, openDetail],
  )

  const onLifecycle = useCallback(
    (row: CampaignRow, action: CampaignLifecycleAction) => {
      if (action === CAMPAIGN_LIFECYCLE_ACTIONS.pause) {
        pause.mutate({ campaignId: row.id })
        return
      }
      start.mutate({ campaignId: row.id, data: null })
    },
    [pause, start],
  )

  const isBusy =
    pause.isPending || start.isPending || stop.isPending || duplicate.isPending

  /** The columns memo hangs off this object, so it keeps one identity for as
   *  long as nothing it reports has moved. */
  const actions = useMemo<CampaignRowActions>(
    () => ({
      onEdit: (row) => openDetail(row.id),
      onDuplicate,
      onLifecycle,
      onStop: (row) => stop.mutate({ campaignId: row.id }),
      onDeleteRequest: setDeleteTarget,
      isBusy,
    }),
    [openDetail, onDuplicate, onLifecycle, stop, isBusy],
  )

  return {
    actions,
    deleteTarget,
    onDeleteCancel: clearTarget,
    onDeleteConfirm: useCallback(() => {
      if (deleteTarget === null) return
      remove.mutate({ campaignId: deleteTarget.id })
    }, [remove, deleteTarget]),
    isDeleting: remove.isPending,
  }
}
